import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["overlay", "video"]
  static values = {
    intervalSeconds: Number,
    placementId: String,
    isVideo: Boolean
  }

  connect() {
    this.pendingOpen = false
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    this.scheduleOpen()
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    this.clearTimers()
  }

  scheduleOpen() {
    this.clearOpenTimer()

    const cooldownKey = `ad_web_modal_cooldown_${this.placementIdValue}`
    const lastClosedAt = sessionStorage.getItem(cooldownKey)
    let delayMs = this.intervalSecondsValue * 1000

    if (lastClosedAt) {
      const elapsedMs = Date.now() - Number(lastClosedAt)
      const intervalMs = this.intervalSecondsValue * 1000
      if (elapsedMs < intervalMs) {
        delayMs = intervalMs - elapsedMs
      }
    }

    this.openTimeout = window.setTimeout(() => this.open(), delayMs)
  }

  open() {
    // Never show the ad (or count an impression) in a hidden tab; defer until
    // the tab becomes visible again.
    if (document.hidden) {
      this.pendingOpen = true
      return
    }

    this.pendingOpen = false
    this.overlayTarget.classList.remove("hidden")
    this.overlayTarget.classList.add("flex")
    this.postEvent("impression")

    if (this.isVideoValue) {
      this.videoAutoCloseTimeout = window.setTimeout(() => this.close(), 10000)
    }
  }

  close() {
    this.overlayTarget.classList.add("hidden")
    this.overlayTarget.classList.remove("flex")
    sessionStorage.setItem(
      `ad_web_modal_cooldown_${this.placementIdValue}`,
      String(Date.now())
    )
    this.clearVideoAutoCloseTimer()
    this.scheduleOpen()
  }

  trackClick() {
    // The anchor's native target="_blank" navigation proceeds; keepalive lets
    // the event request complete even if this page unloads.
    this.postEvent("click")
  }

  handleVisibilityChange() {
    if (!document.hidden && this.pendingOpen) {
      this.open()
    }
  }

  postEvent(type) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(`/api/v1/ads/${this.placementIdValue}/${type}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ platform: "web_coach" }),
      credentials: "same-origin",
      keepalive: true
    }).catch(() => {})
  }

  clearOpenTimer() {
    if (this.openTimeout) {
      window.clearTimeout(this.openTimeout)
      this.openTimeout = null
    }
  }

  clearVideoAutoCloseTimer() {
    if (this.videoAutoCloseTimeout) {
      window.clearTimeout(this.videoAutoCloseTimeout)
      this.videoAutoCloseTimeout = null
    }
  }

  clearTimers() {
    this.clearOpenTimer()
    this.clearVideoAutoCloseTimer()
  }
}
